import React, { useRef } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiDownload, FiFileText, FiPrinter, FiUpload } from 'react-icons/fi';

import {
  PedidoDocumento,
  usePedidoDocumentos,
} from '../services/hooks/usePedidoDocumentos';

type Props = {
  pedidoId: string | number;
};

const formatarTipo = (tipo: string) => {
  if (tipo === 'nfe_xml') return 'XML';
  if (tipo === 'nfe_danfe') return 'DANFE';
  if (tipo === 'etiqueta_envio') return 'Etiqueta';

  return tipo;
};

const obterCorTipo = (tipo: string) => {
  if (tipo === 'nfe_xml') return 'green';
  if (tipo === 'nfe_danfe') return 'blue';
  if (tipo === 'etiqueta_envio') return 'purple';

  return 'gray';
};

const formatarTamanho = (bytes?: number | null) => {
  if (!bytes) return '-';

  const kb = bytes / 1024;

  if (kb < 1024) return `${kb.toFixed(1)} KB`;

  return `${(kb / 1024).toFixed(1)} MB`;
};

const DocumentoItem = ({
  documento,
  onBaixar,
  baixando,
}: {
  documento: PedidoDocumento;
  onBaixar: (documento: PedidoDocumento) => void;
  baixando: boolean;
}) => {
  const itemBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Flex
      bg={itemBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      justify="space-between"
      gap={4}
      direction={{ base: 'column', md: 'row' }}
    >
      <HStack spacing={3} align="flex-start">
        <Box mt={1}>
          <FiFileText />
        </Box>

        <Box>
          <HStack spacing={2} mb={1} flexWrap="wrap">
            <Text fontWeight="semibold">
              {documento.nome_arquivo || `Documento #${documento.id}`}
            </Text>

            <Badge colorScheme={obterCorTipo(documento.tipo)}>
              {formatarTipo(documento.tipo)}
            </Badge>

            <Badge colorScheme="gray">{documento.status || '-'}</Badge>
          </HStack>

          <Text fontSize="xs" color={mutedColor}>
            {documento.mime_type || '-'} ·{' '}
            {formatarTamanho(documento.tamanho_bytes)}
          </Text>
        </Box>
      </HStack>

      <Button
        size="sm"
        leftIcon={<FiDownload />}
        onClick={() => onBaixar(documento)}
        isLoading={baixando}
      >
        Baixar
      </Button>
    </Flex>
  );
};

const SecaoDocumentosPedido: React.FC<Props> = ({ pedidoId }) => {
  const inputXmlRef = useRef<HTMLInputElement | null>(null);
  const inputDanfeRef = useRef<HTMLInputElement | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const {
    documentos,
    carregandoDocumentos,
    anexandoXml,
    anexandoDanfe,
    baixandoDocumento,
    imprimindoEtiqueta,
    anexarXml,
    anexarDanfe,
    baixarDocumento,
    imprimirEtiqueta,
  } = usePedidoDocumentos({ pedidoId });

  const handleSelecionarXml = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const arquivo = event.currentTarget.files?.[0];

    if (!arquivo) return;

    await anexarXml(arquivo);

    if (inputXmlRef.current) {
      inputXmlRef.current.value = '';
    }
  };

  const handleSelecionarDanfe = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const arquivo = event.currentTarget.files?.[0];

    if (!arquivo) return;

    await anexarDanfe(arquivo);

    if (inputDanfeRef.current) {
      inputDanfeRef.current.value = '';
    }
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
      p={6}
    >
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <Box>
          <Text fontWeight="bold">Documentos do pedido</Text>
          <Text fontSize="xs" color={mutedColor}>
            Anexe XML, DANFE e imprima etiquetas quando disponíveis.
          </Text>
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          <Button
            size="sm"
            leftIcon={<FiPrinter />}
            variant="outline"
            onClick={() => imprimirEtiqueta()}
            isLoading={imprimindoEtiqueta}
          >
            Imprimir etiqueta
          </Button>

          <Button
            size="sm"
            leftIcon={<FiUpload />}
            variant="outline"
            onClick={() => inputXmlRef.current?.click()}
            isLoading={anexandoXml}
          >
            Anexar XML
          </Button>

          <Button
            size="sm"
            leftIcon={<FiUpload />}
            colorScheme="blue"
            onClick={() => inputDanfeRef.current?.click()}
            isLoading={anexandoDanfe}
          >
            Anexar DANFE
          </Button>
        </HStack>
      </Flex>

      <input
        ref={inputXmlRef}
        type="file"
        accept=".xml,text/xml,application/xml"
        hidden
        onChange={handleSelecionarXml}
      />

      <input
        ref={inputDanfeRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={handleSelecionarDanfe}
      />

      <Divider mb={5} />

      {carregandoDocumentos ? (
        <Stack spacing={3}>
          <Skeleton h="72px" borderRadius="md" />
          <Skeleton h="72px" borderRadius="md" />
        </Stack>
      ) : documentos.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Nenhum documento anexado para este pedido.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {documentos.map(documento => (
            <DocumentoItem
              key={documento.id}
              documento={documento}
              onBaixar={baixarDocumento}
              baixando={baixandoDocumento}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SecaoDocumentosPedido;
